import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { adsMemberApi, isConflictError } from '../../api/adsMemberApi';
import './ads.css';

export default function PromotionDetailPage() {
  const { promotionId } = useParams();
  const { t } = useTravelTranslation();

  const [promotion, setPromotion] = useState(null);
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    setConflict(false);

    Promise.all([
      adsMemberApi.promotions.get(promotionId),
      Promise.resolve(adsMemberApi.creatives.listByPromotion?.(promotionId)).catch(() => []),
    ])
      .then(([promoData, creatData]) => {
        setPromotion(promoData);
        setCreatives(Array.isArray(creatData) ? creatData : []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.message || '載入推廣詳情失敗');
        setLoading(false);
      });
  }, [promotionId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async () => {
    if (!promotion) return;
    setActionLoading(true);
    setError('');
    setConflict(false);

    try {
      const res = await adsMemberApi.promotions.submit(promotionId, {
        expectedRowVersion: promotion.rowVersion,
      });
      setPromotion(res);
      setActionLoading(false);
    } catch (err) {
      if (isConflictError(err)) {
        setConflict(true);
      } else {
        setError(err?.message || '送出審核失敗');
      }
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!promotion) return;
    setActionLoading(true);
    setError('');
    setConflict(false);

    try {
      const res = await adsMemberApi.promotions.cancel(promotionId, {
        expectedRowVersion: promotion.rowVersion,
      });
      setPromotion(res);
      setActionLoading(false);
    } catch (err) {
      if (isConflictError(err)) {
        setConflict(true);
      } else {
        setError(err?.message || '取消推廣失敗');
      }
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <main className="ads-page" data-testid="promotion-detail-loading">
        <div className="ads-loading">{t('travel.ads.common.loading')}</div>
      </main>
    );
  }

  if (error && !promotion) {
    return (
      <main className="ads-page" data-testid="promotion-detail-error">
        <div className="ads-alert ads-alert--error">{error}</div>
        <Link to="/me/ads" className="ads-btn ads-btn--secondary">
          {t('travel.ads.common.backToAds')}
        </Link>
      </main>
    );
  }

  const status = promotion?.status || 'DRAFT';
  const isDraft = status === 'DRAFT';
  const isSubmitted = status === 'SUBMITTED';
  const isApproved = status === 'APPROVED';
  const isRejected = status === 'REJECTED';

  return (
    <main className="ads-page" data-testid="promotion-detail-page">
      <header className="ads-header">
        <div>
          <h1 className="ads-title">
            推廣申請 #{promotion?.promotionApplicationId}
          </h1>
          <span className={`ads-badge ads-badge--${status}`} data-testid="promotion-status-badge">
            {t(`travel.ads.status.${status}`) || status}
          </span>
        </div>
        <div className="ads-header__actions">
          <Link to="/me/ads" className="ads-btn ads-btn--secondary">
            {t('travel.ads.common.backToAds')}
          </Link>
        </div>
      </header>

      {conflict ? (
        <div className="ads-alert ads-alert--conflict" data-testid="promotion-conflict-alert">
          <span>{t('travel.ads.conflictMessage')}</span>
          <button
            type="button"
            className="ads-btn ads-btn--secondary"
            onClick={load}
            data-testid="btn-promotion-refresh"
          >
            {t('travel.ads.common.refresh')}
          </button>
        </div>
      ) : null}

      {error ? (
        <div className="ads-alert ads-alert--error" data-testid="promotion-action-error">
          {error}
        </div>
      ) : null}

      <section className="ads-detail-section">
        <h4 className="ads-detail-section__title">基本資訊</h4>
        <div className="ads-grid-2">
          <div className="ads-metric">
            <div className="ads-metric__label">推廣申請 ID</div>
            <div className="ads-metric__value" data-testid="promotion-id">
              {promotion?.promotionApplicationId}
            </div>
          </div>
          <div className="ads-metric">
            <div className="ads-metric__label">來源行程 ID</div>
            <div className="ads-metric__value" data-testid="promotion-source-id">
              {promotion?.sourceContentId}
            </div>
          </div>
          <div className="ads-metric">
            <div className="ads-metric__label">版本 (rowVersion)</div>
            <div className="ads-metric__value" data-testid="promotion-row-version">
              {promotion?.rowVersion}
            </div>
          </div>
        </div>

        {isRejected && promotion?.rejectionReason ? (
          <div className="ads-card__rejection" data-testid="promotion-rejection-reason">
            <strong>{t('travel.ads.promotions.rejectionReason')}：</strong>
            {promotion.rejectionReason}
          </div>
        ) : null}
      </section>

      <section className="ads-detail-section">
        <h4 className="ads-detail-section__title">生命週期操作</h4>
        <div className="ads-header__actions">
          {isDraft ? (
            <button
              type="button"
              className="ads-btn ads-btn--primary"
              onClick={handleSubmit}
              disabled={actionLoading}
              data-testid="btn-promotion-submit"
            >
              {t('travel.ads.promotions.submit')}
            </button>
          ) : null}

          {isDraft || isSubmitted ? (
            <button
              type="button"
              className="ads-btn ads-btn--danger"
              onClick={handleCancel}
              disabled={actionLoading}
              data-testid="btn-promotion-cancel"
            >
              {t('travel.ads.promotions.cancel')}
            </button>
          ) : null}

          {isApproved ? (
            <Link
              to={`/me/ads/promotions/${promotionId}/creatives/new`}
              className="ads-btn ads-btn--primary"
              data-testid="btn-promotion-create-creative"
            >
              {t('travel.ads.promotions.createCreative')}
            </Link>
          ) : null}
        </div>
      </section>

      {isApproved ? (
        <section className="ads-detail-section" data-testid="promotion-creatives-section">
          <h4 className="ads-detail-section__title">關聯素材清單</h4>
          {creatives.length === 0 ? (
            <div className="ads-empty">{t('travel.ads.creatives.empty')}</div>
          ) : (
            <div className="ads-list">
              {creatives.map((c) => (
                <Link
                  key={c.creativeId}
                  to={`/me/ads/creatives/${c.creativeId}`}
                  className="ads-card"
                  data-testid={`creative-item-${c.creativeId}`}
                >
                  <div className="ads-card__header">
                    <h5 className="ads-card__title">{c.headline || `素材 #${c.creativeId}`}</h5>
                    <span className={`ads-badge ads-badge--${c.status}`}>
                      {t(`travel.ads.status.${c.status}`) || c.status}
                    </span>
                  </div>
                  {c.summary ? <p>{c.summary}</p> : null}
                </Link>
              ))}
            </div>
          )}
        </section>
      ) : null}
    </main>
  );
}
